import type { IAnyModelType, IStateTreeNode, Instance } from "@gadgetinc/mobx-quick-tree";
import { getRoot } from "@gadgetinc/mobx-quick-tree";
import type {
  ConnectionIndexEntry,
  FieldIndexEntry,
  GlobalActionIndexEntry,
  ModelIndexEntry,
  RoleIndexEntry,
  ShopifyConnectionIndexEntry,
  SourceFileIndexEntry,
} from "./IndexEntry";
import { UnavailableEnvironmentIndexError } from "./IndexEntry";
import type { ApiKeyIndexEntry, ConfigurationVariableIndexEntry } from "./LiveEnvironmentIndex";
import { assert, isLiveRoot } from "./utils";

//This type is implemented by the ServerEnvironmentIndex, LiveEnvironmentIndex and the ReadOnlyEnvironmentIndex
export interface FileIntrospectionIndex {
  files: SourceFileIndexEntry[];
  maybeGetModelIdByFilePath: (filePath: string) => string | undefined;
  getModelFolderById: (apiIdentifier: string) => string | undefined;
  getModelNamespace: (apiIdentifer: string) => string[] | undefined;
  isPathAModelFolder: (path: string) => boolean;
  isPathModelActionsFolder: (path: string) => boolean;
  isPathInModelActionsFolder: (path: string) => boolean;
  fileIsModelSchemaMetadata: (file: { path: string }) => boolean;
  maybeGetModelActionIdByFilePath: (filePath: string) => string | undefined;
  maybeGetGlobalActionIdByFilePath: (filePath: string) => string | undefined;
}

// This type is implemented by ServerEnvironmentIndex on the server and LiveEnvironmentIndex on the clients
export interface AnyEnvironmentIndex extends FileIntrospectionIndex {
  modelsWithoutProblemsData: ModelIndexEntry[];
  models: ModelIndexEntry[];
  files: SourceFileIndexEntry[];
  filesWithoutProblems: SourceFileIndexEntry[];
  connections: ConnectionIndexEntry[];
  shopifyConnection: ShopifyConnectionIndexEntry | undefined;
  configurationVariablesWithoutProblems: ConfigurationVariableIndexEntry[];
  configurationVariables: ConfigurationVariableIndexEntry[];
  apiKeys: ApiKeyIndexEntry[];
  rolesWithoutProblems: RoleIndexEntry[];
  roles: RoleIndexEntry[];
  publishHash: number;
  dependenciesHash: string;
  globalActions: GlobalActionIndexEntry[];
  getModelEntryByKey: (key: string) => ModelIndexEntry | undefined;
  getModelEntryByApiIdentifier: (apiIdentifier: string) => ModelIndexEntry | undefined;
  getConfigurationVariableEntryByApiIdentifier: (apiIdentifier: string) => ConfigurationVariableIndexEntry | undefined;
  getModelFieldByKey: (modelKey: string, fieldKey: string) => FieldIndexEntry | undefined;
  getRoleByKey: (key: string) => RoleIndexEntry | undefined;
  getRoleByName: (name: string) => RoleIndexEntry | undefined;
}

/** Isomorphic get me the EnvironmentIndex function */
export const getFileIntrospectionIndex = (node: IStateTreeNode): FileIntrospectionIndex => {
  const root: Instance<IAnyModelType> = getRoot(node);
  if (root.type == "FullServerEnvironmentRoot") {
    return root.index;
  } else if (isLiveRoot(root)) {
    return assert(root.index.get(), "no index found on live root");
  } else if (root.type == "SimpleTreeTestRoot") {
    return root.index;
  } else if (root.type == "ReadOnlySchemaRoot") {
    return root.index;
  } else {
    throw new UnavailableEnvironmentIndexError(`Unrecognized root type for environment index getting: ${root.type}`);
  }
};

/** Isomorphic get me the EnvironmentIndex function */
export const getEnvironmentIndex = (node: IStateTreeNode): AnyEnvironmentIndex => {
  const root: Instance<IAnyModelType> = getRoot(node);
  if (root.type == "FullServerEnvironmentRoot") {
    return root.index;
  } else if (isLiveRoot(root)) {
    return assert(root.index.get(), "no index found on live root");
  } else {
    throw new UnavailableEnvironmentIndexError(`Unrecognized root type for environment index getting: ${root.type}`);
  }
};
